import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Calendar, CheckCircle2, AlertTriangle, Clock3 } from "lucide-react";

const STATUSES = ["not_started", "in_progress", "filed", "active", "expired", "pending"];

export default function Filings({ adminClientId }) {
  const { user } = useAuth();
  const isAdminView = !!adminClientId;
  const [filings, setFilings] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const url = isAdminView ? `/filings?client_id=${adminClientId}` : "/filings";
    const { data } = await api.get(url);
    setFilings(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [adminClientId]);

  return (
    <div className="space-y-6" data-testid="filings-page">
      {!isAdminView && (
        <div>
          <div className="hs-overline text-blue-700">FILING TRACKER</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Filings</h1>
          <p className="text-sm text-slate-500 mt-2">Real-time progress on every regulatory filing.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filings.map((f) => (
          <FilingCard key={f.id} f={f} canEdit={user?.role === "admin"} onEdit={() => setEditing(f)} />
        ))}
        {filings.length === 0 && <div className="hs-overline">NO FILINGS</div>}
      </div>

      {editing && (
        <EditModal
          filing={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); toast.success("Filing updated"); }}
        />
      )}
    </div>
  );
}

function FilingCard({ f, canEdit, onEdit }) {
  const tone = f.status === "active" || f.status === "filed" ? "hs-tag-success"
    : f.status === "expired" ? "hs-tag-danger"
    : "hs-tag-warn";
  return (
    <div className="hs-card p-6" data-testid={`filing-card-${f.type}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="hs-overline">{f.type}</div>
          <div className="font-display font-bold text-xl mt-1">{f.label}</div>
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 font-mono">
            <Calendar size={12} />
            {f.expires_at ? `Expires ${(f.expires_at).slice(0, 10)}` : "No expiry set"}
          </div>
        </div>
        <span className={`hs-tag ${tone}`}>{(f.status || "").replace("_", " ").toUpperCase()}</span>
      </div>
      {f.notes && <p className="mt-3 text-sm text-slate-500">{f.notes}</p>}

      {/* Timeline */}
      <div className="mt-6 relative pl-6">
        <div className="absolute left-1.5 top-1.5 bottom-1 w-px bg-[#262626]" />
        <div className="hs-overline mb-3">TIMELINE</div>
        <div className="space-y-3">
          {(f.timeline || []).slice().reverse().map((t, i) => (
            <div key={i} className="relative">
              <div className={`absolute -left-[18px] top-1.5 ${i === 0 ? "hs-timeline-node hs-pulse-orange" : "hs-timeline-node hs-timeline-node-dim"}`} />
              <div className="text-sm">{t.event}</div>
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{(t.at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
          ))}
          {(!f.timeline || f.timeline.length === 0) && <p className="text-xs text-slate-500">No events yet.</p>}
        </div>
      </div>

      {canEdit && (
        <div className="mt-4 flex justify-end">
          <button className="hs-btn-secondary text-xs" onClick={onEdit} data-testid={`filing-edit-${f.type}`}>UPDATE STATUS</button>
        </div>
      )}
    </div>
  );
}

function EditModal({ filing, onClose, onSaved }) {
  const [status, setStatus] = useState(filing.status);
  const [expires, setExpires] = useState((filing.expires_at || "").slice(0, 10));
  const [event, setEvent] = useState("");
  const [note, setNote] = useState(filing.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const expires_iso = expires ? new Date(expires + "T00:00:00Z").toISOString() : null;
      await api.put(`/filings/${filing.id}`, {
        status, expires_at: expires_iso, note, timeline_event: event || null,
      });
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="filing-edit-modal">
        <div className="hs-overline">UPDATE · {filing.type}</div>
        <h3 className="font-display font-bold text-xl mt-1">{filing.label}</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="hs-overline">Status</label>
            <select className="hs-input mt-2" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filing-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="hs-overline">Expires on</label>
            <input type="date" className="hs-input mt-2" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="filing-expires-input" />
          </div>
          <div>
            <label className="hs-overline">Notes (visible to client)</label>
            <textarea className="hs-input mt-2 min-h-[64px]" value={note} onChange={(e) => setNote(e.target.value)} data-testid="filing-notes-input" />
          </div>
          <div>
            <label className="hs-overline">Timeline event</label>
            <input className="hs-input mt-2" placeholder="e.g. Plates received from state" value={event} onChange={(e) => setEvent(e.target.value)} data-testid="filing-event-input" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button className="hs-btn-secondary" onClick={onClose}>CANCEL</button>
          <button className="hs-btn-primary" onClick={save} disabled={saving} data-testid="filing-save-button">{saving ? "SAVING…" : "SAVE"}</button>
        </div>
      </div>
    </div>
  );
}
